import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, UserCircle2, Loader2, KeyRound, ShieldCheck, Upload, FileArchive, Shield, ShieldOff } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "./ui/dialog";
import { api } from "../api";

const empty = { name: "", api_id: "", api_hash: "" };

export const AccountsManager = ({ accounts, onRefresh }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [purging, setPurging] = useState(false);

  const [openSections, setOpenSections] = useState({});
  const deadCount = accounts.filter((a) => a.status === "dead").length;
  const activeCount = accounts.filter((a) => a.status === "connected").length;
  const sections = useMemo(() => {
    const map = new Map();
    for (const a of accounts) {
      const id = a.batch_id || "ungrouped";
      if (!map.has(id)) map.set(id, { id, name: a.batch_name || "Ungrouped", items: [] });
      map.get(id).items.push(a);
    }
    return [...map.values()].sort((x, y) => y.items.length - x.items.length || x.name.localeCompare(y.name));
  }, [accounts]);

  const submit = async () => {
    if (!form.name || !form.api_id || !form.api_hash) {
      toast.error("Name, API ID and API Hash are required");
      return;
    }
    if (!file) {
      toast.error("Please select a .session file or a .zip");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("api_id", form.api_id);
      fd.append("api_hash", form.api_hash);
      fd.append("file", file);
      const res = await api.uploadAccount(fd);
      const n = res.created?.length || 0;
      toast.success(`${n} account${n > 1 ? "s" : ""} connected`);
      if (res.errors?.length) {
        toast.warning(`${res.errors.length} session(s) failed to load`);
      }
      setForm(empty);
      setFile(null);
      setOpen(false);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add account");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.deleteAccount(id);
      toast.success("Account removed");
      onRefresh();
    } catch {
      toast.error("Failed to remove account");
    }
  };

  const checkAll = async () => {
    setChecking(true);
    try {
      const res = await api.checkAccounts();
      toast.success(`Check done — ${res.active} active, ${res.dead} dead`);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Account check failed");
    } finally {
      setChecking(false);
    }
  };

  const trashDead = async () => {
    if (!deadCount) {
      toast.info("No dead accounts. Run Check active first.");
      return;
    }
    const ok = window.confirm(`Delete ${deadCount} dead account${deadCount > 1 ? "s" : ""}? Active accounts stay.`);
    if (!ok) return;
    setPurging(true);
    try {
      const res = await api.deleteInactiveAccounts();
      toast.success(`Removed ${res.deleted} dead account${res.deleted === 1 ? "" : "s"}`);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove dead accounts");
    } finally {
      setPurging(false);
    }
  };

  return (
    <section data-testid="accounts-manager-section">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-white">Telegram Accounts</h2>
          <p className="text-sm text-slate-400 mt-1">Upload Telethon <span className="font-mono text-emerald-400">.session</span> files (or a .zip of them).</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="account-check-button"
            onClick={checkAll}
            disabled={checking || accounts.length === 0}
            variant="outline"
            className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 gap-2 rounded-lg"
          >
            {checking ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
            {checking ? "Checking..." : "Check active"}
          </Button>
          <Button
            data-testid="account-trash-dead-button"
            onClick={trashDead}
            disabled={purging || deadCount === 0}
            variant="outline"
            className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 gap-2 rounded-lg"
          >
            {purging ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
            Trash dead{deadCount ? ` (${deadCount})` : ""}
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="account-add-button"
              className="bg-emerald-600 hover:bg-emerald-500 text-white gap-2 rounded-lg"
            >
              <Plus size={16} /> Add Account
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2">
                <KeyRound size={18} className="text-emerald-400" /> Connect Telegram Account
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Upload a Telethon <span className="font-mono">.session</span> file or a <span className="font-mono">.zip</span> of them. api_id & api_hash are required to open the session.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label className="text-slate-300">Section / Group Name</Label>
                <Input
                  data-testid="account-name-input"
                  placeholder="e.g. Zip Batch 1"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-slate-950 border-slate-700 text-slate-100"
                />
                <p className="text-[11px] text-slate-500">This whole upload becomes one selectable section for campaigns.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-slate-300">API ID</Label>
                  <Input
                    data-testid="account-apiid-input"
                    placeholder="1234567"
                    value={form.api_id}
                    onChange={(e) => setForm({ ...form, api_id: e.target.value })}
                    className="bg-slate-950 border-slate-700 text-slate-100 font-mono"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-300">API Hash</Label>
                  <Input
                    data-testid="account-apihash-input"
                    placeholder="abcd1234..."
                    value={form.api_hash}
                    onChange={(e) => setForm({ ...form, api_hash: e.target.value })}
                    className="bg-slate-950 border-slate-700 text-slate-100 font-mono"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300">Session File (.session or .zip)</Label>
                <label
                  htmlFor="session-file"
                  className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-slate-700 bg-slate-950 px-4 py-6 cursor-pointer hover:border-emerald-500/40 transition-colors"
                >
                  {file ? (
                    <>
                      <FileArchive className="text-emerald-400" size={26} />
                      <span className="text-sm text-slate-200 font-mono break-all text-center">{file.name}</span>
                      <span className="text-[11px] text-slate-500">Click to choose a different file</span>
                    </>
                  ) : (
                    <>
                      <Upload className="text-slate-500" size={26} />
                      <span className="text-sm text-slate-400">Click to select a .session or .zip file</span>
                      <span className="text-[11px] text-slate-600">A .zip may contain multiple sessions (bulk add)</span>
                    </>
                  )}
                </label>
                <input
                  id="session-file"
                  data-testid="session-file-input"
                  type="file"
                  accept=".session,.zip"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                data-testid="account-submit-button"
                onClick={submit}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-500 text-white gap-2 w-full sm:w-auto"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
                {saving ? "Loading session..." : "Upload & Connect"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>
      {accounts.length > 0 && (
        <p className="text-xs text-slate-500 mb-4">
          {activeCount} active · {deadCount} dead · {accounts.length} total. Check active, then Trash dead to drop logged-out sessions in one click.
        </p>
      )}

      {accounts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-800 bg-slate-900/40 p-12 text-center">
          <UserCircle2 className="mx-auto text-slate-700" size={40} />
          <p className="mt-3 text-slate-400 text-sm">No accounts connected yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sections.map((section) => {
            const open = !!openSections[section.id];
            const shown = open ? section.items.slice(0, openSections[section.id] === "all" ? section.items.length : 40) : [];
            const deadIn = section.items.filter((a) => a.status === "dead").length;
            return (
              <div key={section.id} className="rounded-xl border border-slate-800 bg-slate-900/90 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setOpenSections((s) => ({ ...s, [section.id]: s[section.id] ? false : 40 }))}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-800/50"
                >
                  <div>
                    <p className="font-semibold text-white">{section.name}</p>
                    <p className="text-xs text-slate-500">{section.items.length} accounts{deadIn ? ` · ${deadIn} dead` : ""}</p>
                  </div>
                  <span className="text-xs text-emerald-400">{open ? "Hide" : "Show"}</span>
                </button>
                {open && (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-3 border-t border-slate-800">
                    {shown.map((a) => (
            <div
              key={a.id}
              data-testid={`account-card-${a.id}`}
              className="rounded-xl border border-slate-800 bg-slate-950/60 p-4"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-full bg-gradient-to-br from-emerald-500/30 to-indigo-500/30 flex items-center justify-center border border-slate-700">
                    <UserCircle2 className="text-emerald-300" size={22} />
                  </div>
                  <div>
                    <p className="font-semibold text-white leading-tight">{a.name}</p>
                    <p className="text-xs text-slate-500">{a.display_name}</p>
                  </div>
                </div>
                <span
                  data-testid={`account-status-badge-${a.id}`}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium ${
                    a.status === "connected"
                      ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                      : "bg-rose-500/10 text-rose-400 border border-rose-500/20"
                  }`}
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-current" /> {a.status === "connected" ? "active" : a.status === "dead" ? "dead" : a.status}
                </span>
              </div>
              <div className="mt-4 space-y-1.5 text-xs font-mono text-slate-400">
                <p>@{a.username || "n/a"}</p>
                <p>{a.phone || "hidden number"}</p>
                {a.status === "dead" && a.check_error && (
                  <p className="text-rose-400/80 normal-case">{a.check_error}</p>
                )}
                {a.proxy_label ? (
                  <p className="flex items-center gap-1.5 text-emerald-400/80"><Shield size={11} /> {a.proxy_label}</p>
                ) : (
                  <p className="flex items-center gap-1.5 text-slate-600"><ShieldOff size={11} /> direct (no proxy)</p>
                )}
              </div>
              <Button
                data-testid={`account-delete-${a.id}`}
                variant="ghost"
                size="sm"
                onClick={() => remove(a.id)}
                className="mt-4 text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 gap-2 px-2"
              >
                <Trash2 size={14} /> Remove
              </Button>
            </div>
                    ))}
                    {section.items.length > shown.length && (
                      <button
                        type="button"
                        onClick={() => setOpenSections((s) => ({ ...s, [section.id]: "all" }))}
                        className="text-sm text-emerald-400 hover:text-emerald-300 px-2 py-3 text-left"
                      >
                        Show all {section.items.length}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};
